import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSection, FormField, TextField, SelectField } from 'modules/Common/form/v2';

// The full CBAM indirect/electricity breakdown, as V2 form fields (D48 — never
// raw HTML inputs). Mirrors the operator modal's "Indirect Embedded Emissions"
// section (Installations/EmissionsDataTab.jsx) field-for-field so the supplier
// draft-review form and the modal capture the SAME data under the SAME field
// names — the transformers stay 1:1 with the backend `indirectDetail` shape.
//
// NOTE (D48 dedup fast-follow): this is currently a faithful copy of that
// section rather than a single shared component, so the operator modal can be
// left untouched until it can be browser-QA'd on both the operator AND supplier
// paths. Consolidating the two onto this one component is the follow-up.
// Module-scope (not exported/reused elsewhere — see mockData.js for the
// separate demo-data copy) — built as a function of `t`, called via useMemo.
const buildElectricitySourceOptions = (t) => [
  {
    value: 'SOE01',
    label: t(
      'freeSupplier:electricitySource.soe01',
      'Direct technical link to electricity generator (SOE01)',
    ),
  },
  {
    value: 'SOE02',
    label: t(
      'freeSupplier:electricitySource.soe02',
      '(Bilateral) power purchase agreement (SOE02)',
    ),
  },
  {
    value: 'SOE03',
    label: t('freeSupplier:electricitySource.soe03', 'Received from the grid (SOE03)'),
  },
];

export default function IndirectEmissionsFields({ disabled = false }) {
  const { t } = useTranslation('freeSupplier');
  const electricitySourceOptions = useMemo(() => buildElectricitySourceOptions(t), [t]);

  return (
    <FormSection
      title={t('indirectFields.sectionTitle', 'Indirect Embedded Emissions')}
      compact
    >
      <FormField
        name="indirectEmissionFactor"
        label={t('indirectFields.emissionFactorLabel', 'Electricity emission factor (tCO2/MWh)')}
        colSpan={6}
      >
        <TextField
          numeric
          disabled={disabled}
        />
      </FormField>

      <FormField
        name="indirectEmissionFactorSourceValue"
        label={t(
          'indirectFields.emissionFactorSourceLabel',
          'Electricity emission factor data source',
        )}
        colSpan={6}
      >
        <TextField disabled={disabled} />
      </FormField>

      <FormField
        name="electricityConsumed"
        label={t(
          'indirectFields.electricityConsumedLabel',
          'Electricity consumed (MWh/tonne of product)',
        )}
        colSpan={6}
      >
        <TextField
          numeric
          disabled={disabled}
        />
      </FormField>

      <FormField
        name="electricitySource"
        label={t('indirectFields.electricitySourceLabel', 'Electricity source')}
        colSpan={6}
      >
        <SelectField
          options={electricitySourceOptions}
          placeholder={t('indirectFields.electricitySourcePlaceholder', 'Select a source')}
          disabled={disabled}
        />
      </FormField>

      <FormField
        name="otherSourceIndication"
        label={t(
          'indirectFields.otherSourceIndicationLabel',
          'Other source indication for (indirect) emission factor',
        )}
        colSpan={6}
      >
        <TextField disabled={disabled} />
      </FormField>

      <FormField
        name="shareDefaultEmissions"
        label={t('indirectFields.shareDefaultEmissionsLabel', 'Share of default emissions (%)')}
        colSpan={6}
      >
        <TextField
          numeric
          disabled={disabled}
        />
      </FormField>
    </FormSection>
  );
}
